import { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { ScopeEditor, ScopeShare, ATTACH_ACCEPT, pickScopeFiles } from "@/components/fs/ScopeEditor";
import { Pencil, Paperclip, FileText, ExternalLink, Trash2, Loader2, CheckCircle2, Download, History, AlertTriangle } from "lucide-react";

const fmt = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

/* One card for the scope on a job (kind="wo") or a project (kind="project"): the text with its
   version, edit → new version, PDF download, previous versions, documents, who it travels to.
   Versions, not overwrites: every save keeps the old wording in the history (iter372). */
export function ScopeCard({ tenantId, kind, id, doc, draftCtx = {}, canAuthor, locked = "", openBids = 0, onChanged, testid = "wo-scope" }) {
  const base = `/tenants/${tenantId}/field-services/${kind === "project" ? "projects" : "wos"}/${id}`;
  const [edit, setEdit] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const fileRef = useRef(null);
  const rev = doc.scope_revision || (doc.scope ? 1 : 0);
  const files = doc.scope_files || [];
  const hist = doc.scope_history || [];
  const share = doc.scope_share || { vendor: true, cc: true };
  const pricedOlder = kind === "wo" && doc.kicked_off_at && doc.amount != null && !doc.price_pending;
  const mayEdit = canAuthor && !locked;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "That didn't save")); }
    setBusy(false);
  };
  const save = () => run(async () => {
    const { data } = await api.put(`${base}/scope`, { scope: edit, note });
    setEdit(null); setNote("");
    if (data.changed) {
      const bits = [`Scope saved as v${data.scope_revision}`];
      if (data.price_reset) bits.push("agreed price reset — the company was asked to re-price");
      else if (data.vendor_told) bits.push("the company was told");
      if (data.bids_reset) bits.push(`${data.bids_reset} bid${data.bids_reset === 1 ? "" : "s"} reset · bids now due ${data.bids_due}`);
      toast.success(bits.join(" · "));
    } else toast.success("No change to the wording");
  });
  const setShare = (s) => run(() => api.put(`${base}/scope`, { share: s }), "Saved who the scope goes to");
  const upload = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    await run(() => api.post(`${base}/scope-file`, fd), `${f.name} attached`);
  };
  const remove = (fid) => run(() => api.delete(`${base}/scope-file/${fid}`), "Removed");
  const pdf = () => run(async () => {
    const r = await api.get(`${base}/scope.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `scope-${doc.ref || doc.title || "scope"}-v${rev || 1}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  });

  return (
    <div data-testid={testid} className="rounded-2xl border border-sand-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-ink/45">
          Scope of work
          {rev > 0 && (
            <span data-testid={`${testid}-version`} className="ml-2 rounded-full bg-sand-100 px-2 py-0.5 font-bold text-ink/60">v{rev}</span>
          )}
          {doc.scope_updated_at && (
            <span data-testid={`${testid}-updated`} className="ml-2 font-semibold normal-case tracking-normal text-ink/40">
              updated {fmt(doc.scope_updated_at)}{doc.scope_updated_by?.name ? ` by ${doc.scope_updated_by.name}` : ""}
            </span>
          )}
          {kind === "wo" && doc.priced_scope_revision && doc.priced_scope_revision !== rev && (
            <span data-testid={`${testid}-priced-older`} className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 font-bold normal-case tracking-normal text-amber-800">priced at v{doc.priced_scope_revision}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {(doc.scope || files.length > 0) && (
            <button data-testid={`${testid}-pdf`} onClick={pdf} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 hover:border-ink/30 disabled:opacity-50">
              <Download className="h-3.5 w-3.5" /> PDF
            </button>
          )}
          {hist.length > 0 && (
            <button data-testid={`${testid}-history`} onClick={() => setShowHist(!showHist)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 hover:border-ink/30">
              <History className="h-3.5 w-3.5" /> {hist.length} earlier version{hist.length === 1 ? "" : "s"}
            </button>
          )}
          {mayEdit && edit === null && (
            <button data-testid={`${testid}-edit`} onClick={() => setEdit(doc.scope || "")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 hover:border-ink/30">
              <Pencil className="h-3.5 w-3.5" /> {doc.scope ? "Edit scope" : "Write the scope"}
            </button>
          )}
          {mayEdit && (
            <>
              <input ref={fileRef} type="file" className="hidden" data-testid={`${testid}-file-input`} accept={ATTACH_ACCEPT} multiple
                onChange={async (e) => { const ok = pickScopeFiles(e.target.files); if (fileRef.current) fileRef.current.value = ""; for (const f of ok) await upload(f); }} />
              <button data-testid={`${testid}-add-file`} onClick={() => fileRef.current?.click()} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 hover:border-ink/30 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Add a document
              </button>
            </>
          )}
        </div>
      </div>

      {edit === null ? (
        doc.scope ? (
          <pre data-testid={`${testid}-text`} className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink/75">{doc.scope}</pre>
        ) : (
          <p data-testid={`${testid}-empty`} className="mt-2 text-sm text-ink/40">
            {kind === "project" ? "No written scope on this one — vendors are bidding off the title and any documents below."
              : "No scope yet — the company has the title and location only."}
          </p>
        )
      ) : (
        <div className="mt-2 space-y-2" data-testid={`${testid}-editor`}>
          <ScopeEditor tenantId={tenantId} kind={kind === "project" ? "project" : "job"} value={edit} onChange={setEdit}
            draftCtx={draftCtx} testid={testid} textareaTestId={`${testid}-input`} />
          {(pricedOlder || openBids > 0) && (
            <div data-testid={`${testid}-bid-warning`} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {pricedOlder && <>This job was confirmed at {doc.amount != null ? `$${Number(doc.amount).toLocaleString()}` : "a price"} for v{doc.priced_scope_revision || rev}. Saving a change <b>resets that price</b> — the company is told and asked to re-price the new scope. </>}
                {openBids > 0 && <>{openBids} bid{openBids === 1 ? "" : "s"} priced the current wording. Saving <b>sets them aside</b>, pushes the deadline out at least three business days and asks every bidder to re-price. </>}
              </span>
            </div>
          )}
          <input data-testid={`${testid}-note`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why the change? (optional — kept with the version)"
            className="w-full rounded-xl border border-sand-300 px-3 py-2 text-xs outline-none" />
          <div className="flex flex-wrap items-center gap-1.5">
            <button data-testid={`${testid}-save`} onClick={save} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-xl bg-redline-500 px-4 py-2 text-xs font-bold text-white hover:bg-redline-600 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save as v{(rev || 0) + 1}
            </button>
            <button data-testid={`${testid}-cancel`} onClick={() => { setEdit(null); setNote(""); }}
              className="rounded-xl border border-sand-300 px-3 py-2 text-xs font-bold text-ink/55 hover:border-ink/30">Cancel</button>
          </div>
        </div>
      )}

      {locked && canAuthor && (
        <div data-testid={`${testid}-locked`} className="mt-2 text-xs font-semibold text-ink/40">{locked}</div>
      )}

      {showHist && hist.length > 0 && (
        <div data-testid={`${testid}-history-list`} className="mt-3 space-y-2">
          {hist.map((h) => (
            <details key={h.rev} data-testid={`${testid}-history-${h.rev}`} className="rounded-xl border border-sand-200 bg-sand-50 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold text-ink/60">
                v{h.rev} · {h.by?.name ? `${h.by.name} · ` : ""}{fmt(h.at)} → replaced {fmt(h.replaced_at)}{h.note ? ` · "${h.note}"` : ""}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-xs leading-relaxed text-ink/65">{h.scope}</pre>
            </details>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testid}-files`}>
          {files.map((sf) => (
            <span key={sf.id} data-testid={`${testid}-file-${sf.id}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 px-2.5 py-1 text-xs font-semibold text-ink/70">
              <a href={sf.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-ink">
                <FileText className="h-3.5 w-3.5" /> {sf.name} <ExternalLink className="h-3 w-3" />
              </a>
              {mayEdit && (
                <button data-testid={`${testid}-file-remove-${sf.id}`} onClick={() => remove(sf.id)} className="text-ink/30 hover:text-danger-600"><Trash2 className="h-3 w-3" /></button>
              )}
            </span>
          ))}
        </div>
      )}

      {(doc.scope || files.length > 0) && (
        <div className="mt-3">
          <ScopeShare kind={kind === "project" ? "project" : "job"} share={share} onChange={setShare} readOnly={!canAuthor || busy} testid={`${testid}-share`} />
        </div>
      )}
    </div>
  );
}
